// Racing line — authored or auto-seeded. Two public entry points:
//
//   densifyAuthoredLine(waypoints, alpha)
//     The runtime path. Takes the user-authored closed-loop waypoints,
//     runs the same Catmull-Rom densifier the centerline / walls use,
//     and returns { dense, tangents, curvature } for AI consumption.
//
//   seedRacingLineWaypoints(centerline, halfWidths, subdivisions)
//     The "Auto-generate" button in the editor. Runs the apex-pull
//     algorithm against the centerline + per-point half-widths to
//     produce SPARSE waypoints the user can drop into track.racingLine
//     and refine. Best-effort starting point — drivers will move
//     waypoints around.
//
//   cornerSpeedLimits(racingLine, gripG)
//     Per-point cornering speed limit from |κ| × g × grip. Used by
//     the AI controller to know when to brake; not used during line
//     authoring.
//
// The apex-pull algorithm is documented in seedRacingLineWaypoints
// itself; it's the same heuristic the previous always-on computed
// line used (apex_κ − anti_κ → tanh-saturated offset). Output quality
// is "decent club racer" — fine as a starting point, not optimal.

import { densifyClosedLoop } from './spline.js';

// Tunings carried over from the always-on computed line. They define
// the SHAPE of the auto-generated seed; authors who don't like the
// default can move waypoints by hand. Comments explain what each
// knob does in case re-tuning is wanted later.

// Apex detection window — narrow, picks up local curvature spikes.
// At SUBS_CENTERLINE=12, 9 points ≈ 0.75 segments of arc length.
const CURV_SMOOTH_WINDOW = 9;

// Anticipation window — wide, decides how far before/after a corner
// the line starts/stops positioning to the outside. ~4 segments worth
// at SUBS=12 — picks up the "next corner" from the surrounding
// straight.
const ANTICIPATION_WINDOW = 51;

// Light cosmetic smoothing of the offset signal — removes window-
// boundary artifacts where the two moving averages slightly disagree.
const OFFSET_SMOOTH_WINDOW = 5;

// Fraction of half-width the seed reaches toward the inside curb at
// apex / outside band on flanking straights. 0.95 keeps tires off the
// dirty bit just past the white line; the user can shift waypoints
// to use the full width if they want.
const APEX_PULL_FRACTION = 0.95;

// Curvature reference for tanh saturation. tanh(deviation / K_REF)
// maps the unbounded curvature signal to (−1, +1). 0.025 (1/m,
// R = 40 m) means moderate corners already saturate — the line goes
// near the inside edge on most race-relevant corners.
const K_REF = 0.025;

// Subdivision count for densifying authored racing-line waypoints.
// Same value the centerline / walls use in track-geometry — close
// enough to the spline's smooth fill that the AI's lookahead point
// won't jump segment to segment.
const AUTHORED_DENSE_SUBS = 14;

// Smoothing on the curvature of the densified authored line — caps
// any kink the user might author with a too-sharp waypoint pair.
const AUTHORED_CURV_SMOOTH = 9;

// ---------- public ----------

// Densify an authored racing line for AI consumption. Returns
//   { dense, tangents, curvature, closed: true,
//     segLengths, cumulative, totalLength }
// where:
//   - dense       — the polyline
//   - tangents    — unit vectors per point
//   - curvature   — signed (+ left, − right)
//   - segLengths  — Euclidean length of segment i → i+1 (closed loop)
//   - cumulative  — arc length at point i, measured from dense[0]
//                   (cumulative[0] = 0)
//   - totalLength — total closed-loop arc length
// The arc-length tables enable O(1) "where is car X on the line"
// lookups, which the AI's traffic awareness uses to compare cars'
// progress around the lap. Null on too-short input.
export function densifyAuthoredLine(waypoints, alpha = 0.5) {
  if (!waypoints || waypoints.length < 4) return null;
  const dense     = densifyClosedLoop(waypoints, AUTHORED_DENSE_SUBS, alpha);
  const tangents  = computeTangents(dense);
  const curvature = movingAverage(computeCurvature(dense, tangents), AUTHORED_CURV_SMOOTH);

  const N = dense.length;
  const segLengths = new Array(N);
  const cumulative = new Array(N);
  let acc = 0;
  for (let i = 0; i < N; i++) {
    cumulative[i] = acc;
    const next = (i + 1) % N;
    segLengths[i] = Math.hypot(dense[next].x - dense[i].x, dense[next].y - dense[i].y);
    acc += segLengths[i];
  }
  const totalLength = acc;

  return {
    dense, tangents, curvature, closed: true,
    segLengths, cumulative, totalLength,
  };
}

// Arc length at a nearestPointOnDense result. Hides the
// segLengths × t interpolation so callers don't fish for indices.
export function arcLengthAtNp(np, line) {
  if (!np || !line || !line.segLengths) return 0;
  return line.cumulative[np.segmentIdx] + np.t * line.segLengths[np.segmentIdx];
}

// Forward arc gap from `fromArc` to `toArc` along the closed loop.
// Always positive (0 → totalLength). Wraps around S/F naturally —
// a car at 95% lap with another at 5% sees a 10% gap forward.
export function forwardArcGap(fromArc, toArc, totalLength) {
  let gap = toArc - fromArc;
  if (gap < 0) gap += totalLength;
  return gap;
}

// Seed sparse racing-line waypoints from the centerline + per-point
// half-widths. Used by the editor's Auto-generate button. Returns an
// array of `{ x, y }` waypoints — one per original centerline waypoint
// (sampled every `subdivisions` indices in the dense centerline).
//
// Algorithm: apex-pull heuristic.
//   apex_κ  = κ smoothed over a NARROW window
//   anti_κ  = κ smoothed over a WIDE window (corner + surrounding straight)
//   deviation = apex_κ − anti_κ
//
// At apex, apex_κ peaks → deviation > 0 → pull toward INSIDE.
// On a straight near a corner, apex_κ ≈ 0 but anti_κ > 0 → deviation
// has the OPPOSITE sign → push toward OUTSIDE.
// Far from any corner: both ≈ 0 → no offset.
//
// Output is a STARTING POINT, not an optimal line. Real racing lines
// are corner-by-corner judgement calls (early/late apex, alternate
// lines through chicanes); this pulls toward the geometric apex.
export function seedRacingLineWaypoints(centerline, halfWidths, subdivisions = 12) {
  const N = centerline.length;
  if (N < 4) return null;

  const baseTangents  = computeTangents(centerline);
  const baseCurvature = computeCurvature(centerline, baseTangents);
  const apexK = movingAverage(baseCurvature, CURV_SMOOTH_WINDOW);
  const antiK = movingAverage(baseCurvature, ANTICIPATION_WINDOW);

  const offsets = new Array(N);
  for (let i = 0; i < N; i++) {
    const halfW  = halfWidths?.[i] ?? 8;
    const maxOff = halfW * APEX_PULL_FRACTION;
    const deviation = apexK[i] - antiK[i];
    offsets[i] = Math.tanh(deviation / K_REF) * maxOff;
  }

  const offSmooth = movingAverage(offsets, OFFSET_SMOOTH_WINDOW);

  // Hard-clamp by half-width AFTER smoothing — the smoothing pass can
  // bleed offset into a narrower section of track, and we don't want
  // the seed leaving the strip.
  for (let i = 0; i < N; i++) {
    const halfW = halfWidths?.[i] ?? 8;
    const limit = halfW * APEX_PULL_FRACTION;
    if (offSmooth[i] >  limit) offSmooth[i] =  limit;
    if (offSmooth[i] < -limit) offSmooth[i] = -limit;
  }

  // Sample at every `subdivisions`-th index — for a dense centerline
  // built with SUBS_CENTERLINE subdivisions per segment, this yields
  // exactly one racing-line waypoint per original centerline waypoint.
  // Sparse output is what the editor wants — authors edit waypoints,
  // not dense polylines.
  const wps = [];
  for (let i = 0; i < N; i += subdivisions) {
    const t  = baseTangents[i];
    const nx = -t.y;
    const ny =  t.x;
    wps.push({
      x: centerline[i].x + nx * offSmooth[i],
      y: centerline[i].y + ny * offSmooth[i],
    });
  }
  return wps;
}

// Cornering speed limit per point: v² = µ × g / |κ|. Tightest corners
// give the lowest v_max; straights (κ ≈ 0) saturate at a large value.
// `gripG` matches DEFAULT_CAR_SPEC.grip (g-units).
export function cornerSpeedLimits(racingLine, gripG, gravity = 9.81) {
  const k = racingLine.curvature;
  const out = new Array(k.length);
  // Floor for |κ| so a perfect straight (κ → 0) doesn't divide by
  // zero. 1e-4 1/m corresponds to a 10 km radius arc — effectively
  // infinite.
  const K_MIN = 1e-4;
  for (let i = 0; i < k.length; i++) {
    out[i] = Math.sqrt((gripG * gravity) / Math.max(Math.abs(k[i]), K_MIN));
  }
  return out;
}

// Pit route — open polyline for cars on a pit-stop. Concatenates the
// entry ribbon + pit-lane spline + exit ribbon into a single dense
// path the AI can pure-pursuit the same way it does the racing line.
// The returned shape mirrors densifyAuthoredLine's so the AI controller
// is line-source agnostic.
//
// Inputs are the densified arrays already cached on geometry:
//   entryDense — ribbon from racing surface to pit-lane waypoint[0]
//   pitDense   — pit-lane spline (open)
//   exitDense  — ribbon from pit-lane waypoint[N-1] back to the
//                racing surface
// Any of the three can be null. Returns null if the pit lane itself
// is missing (no path makes sense without it). Missing entry / exit
// ribbon just truncates that end of the route.
//
// Adjacent input arrays share an endpoint (entryDense[end] equals
// pitDense[0] by construction); we drop the duplicate so segLengths
// stays sane.
//
// `widths` is an optional companion shape:
//   { entry, pit, exit } — entry and exit are scalars (constant ribbon
//                          half-width); pit is a per-vertex array
//                          (matches pitDense indexing).
// When provided, the route emits a `halfWidths` array of the same
// length as `dense`, so edgeProximity can use the local pit-lane
// width when projecting a pitting car against this path.
export function buildPitRoute(entryDense, pitDense, exitDense, widths = null) {
  if (!pitDense || pitDense.length < 2) return null;

  const dense = [];
  const halfWidths = widths ? [] : null;
  // Track where the entry ribbon ends and the exit ribbon begins —
  // the AI uses these to bias the lookahead PAST the entry ribbon so
  // pure-pursuit pulls aggressively toward the pit-lane proper instead
  // of meekly following a near-straight bezier curve into the wall.
  // Indices are -1 when the corresponding ribbon is absent.
  let entryEndIndex = -1;
  let pitLaneEndIndex = -1;
  if (entryDense && entryDense.length >= 2) {
    for (let i = 0; i < entryDense.length; i++) {
      dense.push(entryDense[i]);
      if (halfWidths) halfWidths.push(widths.entry ?? widths.pit?.[0] ?? 2);
    }
    entryEndIndex = dense.length - 1;
  }
  // First pit-lane point: skip if it duplicates the last entry-ribbon
  // point (tolerance 0.01 m — well below dense subdivision spacing).
  for (let i = 0; i < pitDense.length; i++) {
    if (dense.length > 0) {
      const last = dense[dense.length - 1];
      const dx = pitDense[i].x - last.x;
      const dy = pitDense[i].y - last.y;
      if (i === 0 && Math.hypot(dx, dy) < 0.01) continue;
    }
    dense.push(pitDense[i]);
    if (halfWidths) halfWidths.push(widths.pit?.[i] ?? widths.entry ?? 2);
  }
  pitLaneEndIndex = dense.length - 1;
  if (exitDense && exitDense.length >= 2) {
    for (let i = 0; i < exitDense.length; i++) {
      if (dense.length > 0) {
        const last = dense[dense.length - 1];
        const dx = exitDense[i].x - last.x;
        const dy = exitDense[i].y - last.y;
        if (i === 0 && Math.hypot(dx, dy) < 0.01) continue;
      }
      dense.push(exitDense[i]);
      if (halfWidths) halfWidths.push(widths.exit ?? widths.pit?.[widths.pit.length - 1] ?? 2);
    }
  }

  if (dense.length < 2) return null;

  const tangents  = computeTangentsOpen(dense);
  const curvature = movingAverageOpen(computeCurvatureOpen(dense, tangents), AUTHORED_CURV_SMOOTH);

  // segLengths[i] = || dense[i+1] - dense[i] ||. For an OPEN path we
  // keep the array N entries long for index parity with tangents /
  // curvature; the final entry is 0 (no segment from last point).
  const N = dense.length;
  const segLengths = new Array(N).fill(0);
  const cumulative = new Array(N);
  let acc = 0;
  for (let i = 0; i < N; i++) {
    cumulative[i] = acc;
    if (i < N - 1) {
      segLengths[i] = Math.hypot(dense[i + 1].x - dense[i].x, dense[i + 1].y - dense[i].y);
      acc += segLengths[i];
    }
  }
  // Arc value at the entry-ribbon → pit-lane seam, and at the pit-lane
  // → exit-ribbon seam. Null when the corresponding ribbon was absent.
  const entryEndArc = entryEndIndex >= 0 ? cumulative[entryEndIndex] : null;
  const exitStartArc = pitLaneEndIndex < N - 1 ? cumulative[pitLaneEndIndex] : null;
  return {
    dense, tangents, curvature, closed: false,
    segLengths, cumulative, totalLength: acc,
    halfWidths,
    entryEndArc, exitStartArc,
  };
}

// ---------- private helpers ----------

// Closed-loop tangent at each point. Symmetric difference avoids the
// asymmetry of a one-sided derivative — a forward difference biases
// the tangent direction by half a segment, which compounds in the
// curvature pass.
function computeTangents(dense) {
  const N = dense.length;
  const out = new Array(N);
  for (let i = 0; i < N; i++) {
    const prev = dense[(i - 1 + N) % N];
    const next = dense[(i + 1) % N];
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const len = Math.hypot(dx, dy) || 1e-9;
    out[i] = { x: dx / len, y: dy / len };
  }
  return out;
}

// Signed curvature κ = dθ / ds, computed from the cross product of
// neighboring tangents. Positive κ = left turn (CCW); negative = right.
function computeCurvature(dense, tangents) {
  const N = dense.length;
  const out = new Array(N);
  for (let i = 0; i < N; i++) {
    const tPrev = tangents[(i - 1 + N) % N];
    const tNext = tangents[(i + 1) % N];
    const cross = tPrev.x * tNext.y - tPrev.y * tNext.x;
    const pPrev = dense[(i - 1 + N) % N];
    const pNext = dense[(i + 1) % N];
    const ds = Math.hypot(pNext.x - pPrev.x, pNext.y - pPrev.y) || 1e-9;
    out[i] = cross / ds;
  }
  return out;
}

// Open-polyline tangent — endpoints use a one-sided difference (the
// neighboring point IS the endpoint itself, no wrap). Otherwise
// identical to the closed version.
function computeTangentsOpen(dense) {
  const N = dense.length;
  const out = new Array(N);
  for (let i = 0; i < N; i++) {
    const prev = i === 0     ? dense[i] : dense[i - 1];
    const next = i === N - 1 ? dense[i] : dense[i + 1];
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const len = Math.hypot(dx, dy) || 1e-9;
    out[i] = { x: dx / len, y: dy / len };
  }
  return out;
}

// Open-polyline curvature — endpoints have no two-sided neighborhood,
// so curvature defaults to 0 there (drive straight on/off the pit
// lane; back-prop wouldn't pre-brake for a curvature anomaly at the
// very start or end anyway).
function computeCurvatureOpen(dense, tangents) {
  const N = dense.length;
  const out = new Array(N);
  for (let i = 0; i < N; i++) {
    if (i === 0 || i === N - 1) {
      out[i] = 0;
      continue;
    }
    const tPrev = tangents[i - 1];
    const tNext = tangents[i + 1];
    const cross = tPrev.x * tNext.y - tPrev.y * tNext.x;
    const ds = Math.hypot(dense[i + 1].x - dense[i - 1].x, dense[i + 1].y - dense[i - 1].y) || 1e-9;
    out[i] = cross / ds;
  }
  return out;
}

// Open-polyline moving average — clamps the window at the endpoints
// instead of wrapping. Otherwise identical (count-normalized so the
// boundaries don't underweight).
function movingAverageOpen(arr, window) {
  const N = arr.length;
  const out = new Array(N);
  const half = Math.floor(window / 2);
  for (let i = 0; i < N; i++) {
    let sum = 0;
    let count = 0;
    for (let k = -half; k <= half; k++) {
      const j = i + k;
      if (j < 0 || j >= N) continue;
      sum += arr[j];
      count++;
    }
    out[i] = count > 0 ? sum / count : arr[i];
  }
  return out;
}

// Moving average over a closed loop. Window should be odd; we use
// floor(window / 2) on each side.
function movingAverage(arr, window) {
  const N = arr.length;
  const out = new Array(N);
  const half = Math.floor(window / 2);
  for (let i = 0; i < N; i++) {
    let sum = 0;
    let count = 0;
    for (let k = -half; k <= half; k++) {
      const j = ((i + k) % N + N) % N;
      sum += arr[j];
      count++;
    }
    out[i] = sum / count;
  }
  return out;
}
